import React, { createContext, useState, useEffect } from "react";
import companyApi from "../api/companyApi";

const CompanyContext = createContext();

export const CompanyProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);

        // Check user role first - SuperAdmin doesn't need company context
        const userRole = localStorage.getItem("role");
        if (userRole === "superadmin") {
          setCompany({
            name: "Travel CRM SuperAdmin",
            logoUrl: "/logo-superadmin.png",
            themeColor: "#1890ff",
          });
          document.documentElement.style.setProperty(
            "--brand-color",
            "#1890ff",
          );
          return;
        }

        // For non-superadmin users, get companyId with comprehensive checks
        let companyId = localStorage.getItem("companyId");

        // Wait for user data to be fully loaded before proceeding
        const userData = localStorage.getItem("user");

if (!userData && !userRole) {
  console.log("Login page - skipping company fetch");
  setLoading(false);
  return;
}

        // Try to get companyId from token if not in localStorage
        if (!companyId) {
          const token = localStorage.getItem("token");
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split(".")[1]));
              companyId =
                payload.orgId || payload.companyId || payload.organization;

              // Store companyId in localStorage for future use
              if (companyId) {
                localStorage.setItem("companyId", companyId);
                console.log("✅ Retrieved companyId from token:", companyId);
              }
            } catch (error) {
              console.warn("Failed to parse token for companyId:", error);
            }
          }
        }

        // If still no companyId, wait for backend to assign one during login
        if (!companyId && userRole === "admin") {
          console.warn(
            "⚠️ Admin user without companyId - backend should assign one during login",
          );

          // Create temporary fallback company to prevent crashes
          const fallbackCompany = {
            _id: "temp-company",
            name: "Travel CRM Company",
            logoUrl: "/logo-default.png",
            themeColor: "#1890ff",
            isActive: true,
          };
          setCompany(fallbackCompany);
          document.documentElement.style.setProperty(
            "--brand-color",
            "#1890ff",
          );
          return;
        }

        // Skip fetch if no companyId available
        if (!companyId) {
          console.log("⏭️ No companyId available, skipping company fetch");
          setLoading(false);
          return;
        }

        // Fetch company by companyId
        try {
          const data = await companyApi.getCompanyProfile(companyId);
          setCompany(data);

          // Ensure theme variable
          const theme = data?.themeColor || "#2563eb";
          document.documentElement.style.setProperty("--brand-color", theme);
        } catch (error) {
          console.warn(
            "Company not found in database, using fallback company data",
          );
          // Create fallback company data for valid companyId
          const fallbackCompany = {
            _id: companyId,
            name: `Company ${companyId}`,
            logoUrl: "/logo-default.png",
            themeColor: "#1890ff",
            isActive: true,
          };
          setCompany(fallbackCompany);
          document.documentElement.style.setProperty(
            "--brand-color",
            "#1890ff",
          );
        }
      } catch (err) {
        console.error("Failed to fetch company:", err);
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, []);

  return (
    <CompanyContext.Provider value={{ company, setCompany, loading }}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
